import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Grid } from '@mui/material';
import BenefitCard from './Components/BenefitCard';
import { toast } from 'react-toastify';
import PersonDetailsForm from './Components/PersonalDetails';
import { GstUserPanDataFetch } from '../MyBenefits/APIs/action';
import { useLocation, useNavigate } from 'react-router-dom';
import DetailCard from './Components/DetailCard';
import { IDLength, UserFileDict } from '../../../utils/settings';
import {
  AddNomineeAction,
  deleteBeneficiaryAction,
  getBeneficiaryDetails,
  getPancardImageAction,
  updateNomineeAction,
} from './APIs/action';
import ConfirmDeleteModal from './Components/DeleteModal';
import FirmDetails from './Components/FirmDetails';
import { calculateAgesAndFlags } from '../../Utils/AgeCalc';
import {
  compressImage,
  getOptimalCompressionSettings,
  validateImageType,
} from '../../Utils/imageCompression';
import { validateIsAlphaNumeric, validatePersonalPAN } from '../../Utils';

const AddNominee = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [panLoader, setPanLoader] = useState(false);
  const [beneficiaryData, setBeneficiaryData] = useState([]);
  const [showData, setShowData] = useState(false);
  const insuranceType = location?.state?.insurance_type;
  const selectedCompanyEntity = location?.state?.selectedCompanyEntity;
  const beneficiary_id = location?.state?.beneficiary_id;
  const isEdit = location?.state?.isEdit;
  const navigateTo = location?.state?.navigate_to;
  const nomineeData = location?.state?.nominee_data || {};
  const [submitLoader, setSubmitLoader] = useState(false);
  const [nominee, setNominee] = useState({
    insuranceType: insuranceType,
    id_type: UserFileDict.PAN_CARD,
    id_number: '',
    id_str_base64: null,
    full_name: '',
    dob: '',
    age: '',
    phone_number: '',
    relation: '',
    confirm: false,
    file_name: null,
    gender: '',
  });
  const [isImageUpdated, setIsImageUpdated] = useState(false);
  const [ageBar, setAgeBar] = useState(false);
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState({});
  const [panError, setPanError] = useState('');
  const getBeneficairyDetails = async (selectedCompanyEntity) => {
    setLoader(true);
    const { data, error, message } = await getBeneficiaryDetails(
      beneficiary_id,
      insuranceType,
      selectedCompanyEntity
    );
    setLoader(false);
    if (!error) {
      setBeneficiaryData(data);
    } else {
      toast.error(message);
    }
  };

  const handleChange = (field, value) => {
    setNominee((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'dob') {
        const { age, ageFlag } = calculateAgesAndFlags([value])[0];
        updated.age = age;
        setAgeBar(ageFlag);
      }
      return updated;
    });
  };
  const [compressedSize, setCompressedSize] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) {
      setNominee((prev) => ({
        ...prev,
        id_str_base64: null,
        file_name: null,
      }));
      return;
    }

    const file = files[0];
    const acceptedFileTypes = ['image/png', 'image/jpeg', 'image/heic'];
    if (!validateImageType(file, acceptedFileTypes)) {
      toast.error('Invalid file type. Please upload PNG, JPEG or HEIC.');
      return;
    }

    const filename = file.name;
    setIsCompressing(true);
    try {
      const optimalSettings = await getOptimalCompressionSettings();
      const compressionOptions = {
        maxWidth: 1024,
        maxHeight: 1024,
        quality: 0.7,
        ...optimalSettings,
      };

      const result = await compressImage(file, compressionOptions);
      setNominee((prev) => ({
        ...prev,
        id_str_base64: result.base64Data,
        file_name: filename,
      }));
      setIsImageUpdated(true);
      setCompressedSize(result.compressedSize);
    } catch (error) {
      toast.error('Failed to upload image. Try again.');
    } finally {
      setIsCompressing(false);
    }
  };
  const panInputRef = useRef(null);
  const validateFields = () => {
    const newErrors = {};
    if (!nominee.relation) newErrors.relation = 'Relation is required';
    if (!nominee.id_number) {
      newErrors.id_number = 'PAN number is required';
      panInputRef.current?.focus();
    } else if (panError) {
      newErrors.id_number = panError;
      panInputRef.current?.focus();
    }
    if (!nominee.gender) newErrors.gender = 'Gender is required';
    if (!nominee.dob) newErrors.dob = 'Date of birth is required';
    if (nominee.age < 18) {
      setPanError('Age is less than 18, input another PAN.');
      panInputRef.current?.focus();
      return false;
    }
    if (!nominee.phone_number) {
      newErrors.phone_number = 'Phone number is required';
    } else if (nominee.phone_number.length !== 10) {
      newErrors.phone_number = 'Phone number must be 10 digits';
    }
    if (!nominee.confirm)
      newErrors.confirm = 'Please confirm the details are correct';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;
    let baseNomineeData, EditPyload, original, payload;
    if (isEdit && nomineeData?.id) {
      original = flattenBeneficiaryData(nomineeData);
      EditPyload = {
        id_type: nominee.id_type !== original.id_type ? nominee.id_type : null,
        file_name: isImageUpdated ? nominee.file_name : null,
        id_str_base64: isImageUpdated ? nominee.id_str_base64 : null,
        relation: nominee.relation !== original.relation ? nominee.relation : null,
        phone_no: nominee.phone_number !== original.phone_number ? nominee.phone_number : null,
        
      };
      payload = {
        company_esi_id: selectedCompanyEntity,
        beneficiary_id,
        nominee_id: nomineeData?.id,
        ...EditPyload,
      };
    } else {
      baseNomineeData = {
        id_type: nominee.id_type,
        id_number: nominee.id_number,
        id_str_base64: nominee.id_str_base64,
        file_name: nominee.file_name,
        full_name: nominee.full_name || '',
        dob: nominee.dob,
        age: nominee.age,
        phone_number: nominee.phone_number,
        relation: nominee.relation,
        gender: nominee.gender || '',
      };
      payload = {
        company_esi_id: selectedCompanyEntity,
        beneficiary_id,
        insurance_id: insuranceType,
        nominee: baseNomineeData,
      };
    }
    
    setSubmitLoader(true);
    let response;
    if (isEdit && nomineeData?.id) {
      response = await updateNomineeAction(payload);
    } else {
      response = await AddNomineeAction(payload);
    }
    setSubmitLoader(false);
    const { error, message } = response;
    if (!error) {
      toast.success(message);
      if (isEdit && nomineeData?.id) {
        navigateTo &&
          navigate(navigateTo, {
            state: {
              insurance_type: insuranceType,
              selectedCompanyEntity: selectedCompanyEntity
            },
          });
      } else {
        navigate('/beneficiary/detail', {
          state: {
            insurance_type: insuranceType,
            selectedCompanyEntity: selectedCompanyEntity
          },
        });
      }
    } else {
      toast.error(message);
    }
  };
  function flattenBeneficiaryData(data) {
    const flat = {
      firm_owner_name: data.firm_owner_name,
      id: data.id,
    };
    data.details.forEach(item => {
      switch (item.display_name) {
        case "Name":
          flat.full_name = item.display_value;
          break;
        case "Gender":
          flat.gender = item.display_value;
          break;
        case "D.O.B":
          flat.dob = item.display_value;
          break;
        case "Mobile No.":
          flat.phone_number = item.display_value;
          break;
        case "Relation":
          flat.relation = item.display_value;
          break;
        case "PAN":
          flat.id_number = item.display_value;
          break;
        default:
          break;
      }
    });
    return flat;
  }
  const onPanBlur = async (panNumber) => {
    setErrors({});
    setPanError('');
    setShowData(false);
    const isValidPan =
      validateIsAlphaNumeric(panNumber) && panNumber.length === IDLength.PAN;
    if (!panNumber) {
      setPanError('PAN number is required');
      return;
    } else if (!isValidPan) {
      setPanError('Incorrect format added, input PAN again.');
      return;
    } else if (!validatePersonalPAN(panNumber)) {
      setPanError('Please enter Personal PAN');
      return;
    }
    if (isValidPan && validatePersonalPAN(panNumber)) {
      setNominee((prevState) => ({
        ...prevState,
        confirm: false,
        id_str_base64: null,
        full_name: '',
        dob: '',
        age: '',
        phone_number: '',
        file_name: null,
        gender: '',
      }));
    }
    setPanLoader(true);
    const { error, message, data } = await GstUserPanDataFetch(
      panNumber,
      false,
    );
    setPanLoader(false);
    if (!error) {
      setNominee((prev) => ({
        ...prev,
        gender: data?.gender || '',
        full_name: data?.name || '',
        dob: data?.date_of_birth,
        age: calculateAgesAndFlags([data?.date_of_birth])[0].age,
      }));
      setShowData(true);
    } else {
      setPanError(message || 'Something went wrong while verifying PAN');
    }
  };
  const getPancardImage = async (beneficiary_id, selectedCompanyEntity) => {
    const { error, message, data } = await getPancardImageAction(
      beneficiary_id,
      selectedCompanyEntity
    );
    if (!error) {
      setNominee((prev) => ({
        ...prev,
        id_str_base64: data?.pancard_image,
        file_name: data?.file_name,
      }));
    }
  };
  useEffect(() => {
    isEdit && setShowData(true);
    isEdit && nomineeData && getPancardImage(nomineeData?.id, selectedCompanyEntity);
    if (isEdit && nomineeData?.details?.length) {
      const detailsMap = {};
      nomineeData.details.forEach((item) => {
        if (item.display_name === 'Name') {
          detailsMap.full_name = item.display_value;
        } else if (item.display_name === 'D.O.B') {
          detailsMap.dob = item.display_value;
          detailsMap.age = calculateAgesAndFlags([item.display_value])[0].age;
        } else if (item.display_name === 'Mobile No.') {
          detailsMap.phone_number = item.display_value;
        } else if (item.display_name === 'Gender') {
          detailsMap.gender = item.display_value;
        } else if (item.display_name === 'Relation') {
          detailsMap.relation = item.display_value;
        } else if (item.display_name === 'PAN') {
          detailsMap.id_number = item.display_value;
        } else if (item.display_name === 'Relation') {
          const matchedRelation = relations.find(
            (rel) => rel.label === item.display_value,
          );
          if (matchedRelation) {
            detailsMap.relation = matchedRelation.value;
          }
        }
      });
      setNominee((prev) => ({
        ...prev,
        ...detailsMap,
        id_type: UserFileDict.PAN_CARD,
        confirm: true,
      }));
    }
  }, [isEdit, nomineeData]);
  const handleEdit = (item) => {
    navigate('/beneficiary/add-beneficiary', {
      state: {
        insurance_type: insuranceType,
        beneficiary_data: item,
        isEdit: true,
        navigate_to: '/beneficiary/add-nominee',
        selectedCompanyEntity: selectedCompanyEntity
      },
    });
  };
  const [deleteTarget, setDeleteTarget] = useState(beneficiary_id);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNomineeForm, setShowNomineeForm] = useState(false);
  const handleDelete = (beneficiary_id, selectedCompanyEntity) => {
    setDeleteTarget(beneficiary_id, selectedCompanyEntity);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    setLoading(true);
    const { error, message } = await deleteBeneficiaryAction(
      deleteTarget,
      insuranceType,
      selectedCompanyEntity
    );
    setLoading(false);
    setShowDeleteModal(false);
    if (!error) {
      toast.success(message);
      navigate('/beneficiary/add-beneficiary', {
        state: {
          insurance_type: insuranceType,
          selectedCompanyEntity: selectedCompanyEntity
        },
      });
    } else {
      toast.error(message || 'Delete failed');
    }
  };
  useEffect(() => {
    if (beneficiary_id && insuranceType && selectedCompanyEntity) {
      getBeneficairyDetails(selectedCompanyEntity);
    } else {
      navigate('/beneficiary');
    }
  }, [beneficiary_id, insuranceType, selectedCompanyEntity]);
  const {
    benefit_card,
    selected_firm,
    beneficiary_details,
    relations,
    confirmation_text,
  } = beneficiaryData;

  if (!beneficiaryData) {
    return (
      <CircularProgress
        size={24}
        sx={{ color: '#FFA525' }}
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 2 }}>
      <Box sx={{ mb: 5 }}>
        {!loader ? (
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <Grid
              container
              spacing={2}
              sx={{ alignItems: 'stretch', mb: 3 }}
            >
              <Grid
                item
                xs={12}
                sm={6}
              >
                <BenefitCard
                  item={benefit_card}
                  sx={{ height: '100%' }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
              >
                <Box
                  sx={{
                    border: '1px solid #D9D9D9',
                    p: 2,
                    borderRadius: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  {selected_firm &&
                    selected_firm.map((detail, i) => (
                      <React.Fragment key={i}>
                        <FirmDetails detail={detail} />
                      </React.Fragment>
                    ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <CircularProgress
              size={24}
              sx={{ color: '#FFA525' }}
            />
          </Box>
        )}
        <Box sx={{ border: '1px solid #D9D9D9', p: 2, borderRadius: 3, mb: 2 }}>
          {!loader && (
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                sm={12}
              >
                <DetailCard
                  details={beneficiary_details}
                  handleDelete={handleDelete}
                  handleEdit={handleEdit}
                  selectedCompanyEntity={selectedCompanyEntity}
                />
              </Grid>
            </Grid>
          )}
        </Box>
        {showNomineeForm || isEdit ? (
          <Box
            sx={{ border: '1px solid #D9D9D9', p: 2, borderRadius: 3, mb: 2 }}
          >
            <Typography
              variant='body1'
              sx={{
                fontWeight: 600,
                fontSize: '20px',
                mb: 3,
                lineHeight: '1.8',
              }}
            >
              Add Nominee Details
            </Typography>
            <PersonDetailsForm
              person={nominee}
              setPerson={(field, value) => handleChange(field, value)}
              relations={relations}
              handleUpload={handleUpload}
              onPanBlur={onPanBlur}
              onSubmit={handleSubmit}
              confirmation_text={confirmation_text}
              panLoader={panLoader}
              errors={errors}
              showData={showData}
              isEdit={isEdit}
              panError={panError}
              panInputRef={panInputRef}
            />

            <Box sx={{ display: 'flex', mt: 3 }}>
              <Button
                variant='contained'
                color='primary'
                disabled={submitLoader || !!panError || panLoader}
                onClick={handleSubmit}
                sx={{
                  textTransform: 'none',
                  px: 3,
                  position: 'relative',
                  width: { md: 'fit-content', xs: '100%' },
                }}
              >
                {submitLoader ? (
                  <CircularProgress
                    size={20}
                    color='inherit'
                  />
                ) : (
                  'Save'
                )}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', mt: 3 }}>
            <Button
              variant='contained'
              color='primary'
              onClick={() => setShowNomineeForm(true)}
              sx={{
                textTransform: 'none',
                px: 3,
                position: 'relative',
                width: { md: 'fit-content', xs: '100%' },
              }}
            >
              Add Nominee
            </Button>
          </Box>
        )}
      </Box>
      <ConfirmDeleteModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        loading={loading}
      />
    </Box>
  );
};

export default AddNominee;
